import React, { useState, useEffect } from 'react';
import { getAllVHNDData } from '../services/authService';
import '.././styles/VHNDEventSummary.css';
import VhndTabs from '../components/VhndTabs';

const VHNDEventSummary = () => { 
  const [vhndData, setVhndData] = useState([]);
  const [loading, setLoading] = useState(true);  
  const [error, setError] = useState(null);
  const [page] = useState(1); // Fixed to page 1 for single event; adjust if needed
  const perPage = 10;

  // Fetch VHND data from API
  useEffect(() => {
    const fetchVHNDData = async () => {
      try {
        setLoading(true);
        const response = await getAllVHNDData(page, perPage);
        console.log('API Response:', response);
        if (response && response.data) {
          if (response.data.status && response.data.data) {
            setVhndData(response.data.data);
          } else if (Array.isArray(response.data)) {
            setVhndData(response.data);
          } else {
            setError('Invalid API response structure');
          }
        } else {
          setError('No data received from API');
        }
      } catch (err) {
        console.error('API Error:', err);
        setError('Error fetching data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchVHNDData();
  }, [page]);

  // Log vhndData when it updates
  useEffect(() => {
    console.log('vhndData:', vhndData);
  }, [vhndData]);

  // Process VHND data into desired format for VHNDEventSummary
  const processVHNDData = (data) => {
    if (!data || data.length === 0) return null; // Return null if no data
    // Select the first event (or most recent; adjust sorting if needed)
    const vhnd = data[0];
    return {
      date: vhnd.vhnd_date
        ? new Date(vhnd.vhnd_date).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : 'N/A',
      location: vhnd.location || 'N/A',
      timing: `${vhnd.start_time ? vhnd.start_time.slice(0, 5) : 'N/A'} - ${
        vhnd.end_time ? vhnd.end_time.slice(0, 5) : 'N/A'
      }`,
      totalAttendance: (vhnd.VhndParticipations?.length || 0).toString(),
      services: (vhnd.VhndParticipations?.[0]?.VhndParticipantServices || []).map((s, index) => ({
        service: s?.service_type || 'N/A',
        beneficiaries: Math.floor(Math.random() * 20) + 5, // Placeholder; replace with API data
        status: 'Completed',
      })) || [
        { service: 'Immunization', beneficiaries: 24, status: 'Completed' },
        { service: 'ANC Check-up', beneficiaries: 12, status: 'Completed' },
      ],
      officials: (vhnd.VhndStaffAttendances || []).map((staff) => ({
        name: staff?.official_name || 'N/A',
        designation: staff?.official_role || 'N/A',
      })) || [
        { name: 'Dr. Unknown', designation: 'Medical Officer' },
        { name: 'Nurse Unknown', designation: 'ANM' },
      ],
      attendanceAnalysis: {
        pregnantWomen: Math.floor(Math.random() * 10) + 10, // Placeholder
        children: Math.floor(Math.random() * 20) + 20, // Placeholder
        women: Math.floor(Math.random() * 10) + 5, // Placeholder
        others: Math.floor(Math.random() * 5) + 5, // Placeholder
      },
      supplies: [
        { item: 'OPV Vaccine', quantity: '30 doses' },
        { item: 'DPT Vaccine', quantity: '25 doses' },
        // Add more based on API data if available
      ], // Static; replace with API data
      issues: [
        {
          title: 'Vaccine Shortage',
          priority: 'High Priority',
          description: 'Ran out of Hepatitis B vaccine. Need to arrange for next VHND.',
          tag: 'Follow-up Required',
          tagColor: 'orange',
        },
        // Static; replace with API data
      ],
      notes: 'The VHND event was successfully conducted with good participation. ' +
        'Special focus on immunization and growth monitoring. ' +
        'Community requested more family planning info for next session.', // Static; replace with API data
    };
  };

  // Processed data (single event)
  const eventData = processVHNDData(vhndData);

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-600">Error: {error}</div>;
  if (!eventData) return <div>No VHND data available.</div>;

  return (
    <>
      <div className="page-header flex justify-between items-center mb-6">
        <div className="title-section flex items-center">
          <button className="back-button p-2 rounded-full bg-gray-200 hover:bg-gray-300 mr-4">
            <span className="material-icons">arrow_back</span>
          </button>
          <div className="page-title text-2xl font-bold">VHND Event Summary</div>
        </div>
        <div className="action-buttons flex space-x-2">
          <button className="outline-button flex items-center border border-blue-600 text-blue-600 px-4 py-2 rounded hover:bg-blue-50">
            <span className="material-icons button-icon mr-2">print</span>
            Print Report
          </button>
          <button className="primary-button flex items-center bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            <span className="material-icons button-icon mr-2">edit</span>
            Edit Details
          </button>
        </div>
      </div>
      <VhndTabs/>
      <div className="section-card bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="section-title flex items-center text-lg font-semibold mb-4">
          <span className="material-icons section-icon mr-2">event</span>
          VHND Event Details
        </div>
        <div className="info-grid grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="info-card flex flex-col items-center bg-gray-50 p-4 rounded-lg">
            <div className="info-card-icon mb-2">
              <span className="material-icons text-blue-600">calendar_today</span>
            </div>
            <div className="info-card-value font-semibold">{eventData.date}</div>
            <div className="info-card-label text-gray-600">Event Date</div>
          </div>
          <div className="info-card flex flex-col items-center bg-gray-50 p-4 rounded-lg">
            <div className="info-card-icon mb-2">
              <span className="material-icons text-blue-600">location_on</span>
            </div>
            <div className="info-card-value font-semibold">{eventData.location}</div>
            <div className="info-card-label text-gray-600">Location</div>
          </div>
          <div className="info-card flex flex-col items-center bg-gray-50 p-4 rounded-lg">
            <div className="info-card-icon mb-2">
              <span className="material-icons text-blue-600">schedule</span>
            </div>
            <div className="info-card-value font-semibold">{eventData.timing}</div>
            <div className="info-card-label text-gray-600">Timing</div>
          </div>
          <div className="info-card attendance-card flex flex-col items-center bg-gray-50 p-4 rounded-lg">
            <div className="info-card-icon mb-2">
              <span className="material-icons text-blue-600">people</span>
            </div>
            <div className="info-card-value font-semibold">{eventData.totalAttendance}</div>
            <div className="info-card-label text-gray-600">Total Attendance</div>
          </div>
        </div>
        <div className="flex-row grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex-column">
            <div className="section-title flex items-center text-lg font-semibold mb-4">
              <span className="material-icons section-icon mr-2">medical_services</span>
              Services Provided
            </div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-200">
                  <th className="p-3 text-left">Service</th>
                  <th className="p-3 text-left">Beneficiaries</th>
                  <th className="p-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {eventData.services.map((item, index) => (
                  <tr key={index} className="border-b">
                    <td className="p-3">{item.service}</td>
                    <td className="p-3">{item.beneficiaries}</td>
                    <td className="p-3">
                      <span className="status-tag status-completed bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex-column">
            <div className="section-title flex items-center text-lg font-semibold mb-4">
              <span className="material-icons section-icon mr-2">health_and_safety</span>
              Health Officials Present
            </div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-200">
                  <th className="p-3 text-left">Name</th>
                  <th className="p-3 text-left">Designation</th>
                </tr>
              </thead>
              <tbody>
                {eventData.officials.map((official, index) => (
                  <tr key={index} className="border-b">
                    <td className="p-3">{official.name}</td>
                    <td className="p-3">{official.designation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="section-card bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="section-title flex items-center text-lg font-semibold mb-4">
          <span className="material-icons section-icon mr-2">analytics</span>
          Attendance Analysis
        </div>
        <div className="chart-container bg-gray-100 p-4 rounded-lg mb-4">
          <div className="chart-placeholder text-center text-gray-600">[Attendance by Category Chart]</div>
        </div>
        <div className="metrics-container grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="metric-card flex items-center bg-gray-50 p-4 rounded-lg">
            <div className="metric-icon mr-3">
              <span className="material-icons text-pink-600">pregnant_woman</span>
            </div>
            <div className="metric-content">
              <div className="metric-value font-semibold">{eventData.attendanceAnalysis.pregnantWomen}</div>
              <div className="metric-label text-gray-600">Pregnant Women</div>
            </div>
          </div>
          <div className="metric-card flex items-center bg-gray-50 p-4 rounded-lg">
            <div className="metric-icon blue mr-3">
              <span className="material-icons text-blue-600">child_care</span>
            </div>
            <div className="metric-content">
              <div className="metric-value font-semibold">{eventData.attendanceAnalysis.children}</div>
              <div className="metric-label text-gray-600">Children (0-5 years)</div>
            </div>
          </div>
          <div className="metric-card flex items-center bg-gray-50 p-4 rounded-lg">
            <div className="metric-icon purple mr-3">
              <span className="material-icons text-purple-600">woman</span>
            </div>
            <div className="metric-content">
              <div className="metric-value font-semibold">{eventData.attendanceAnalysis.women}</div>
              <div className="metric-label text-gray-600">Women (15-49 years)</div>
            </div>
          </div>
          <div className="metric-card flex items-center bg-gray-50 p-4 rounded-lg">
            <div className="metric-icon orange mr-3">
              <span className="material-icons text-orange-600">person</span>
            </div>
            <div className="metric-content">
              <div className="metric-value font-semibold">{eventData.attendanceAnalysis.others}</div>
              <div className="metric-label text-gray-600">Others</div>
            </div>
          </div>
        </div>
        <div className="flex-row grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex-column">
            <div className="section-title flex items-center text-lg font-semibold mb-4">
              <span className="material-icons section-icon mr-2">inventory</span>
              Supplies Utilized
            </div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-200">
                  <th className="p-3 text-left">Item</th>
                  <th className="p-3 text-left">Quantity</th>
                </tr>
              </thead>
              <tbody>
                {eventData.supplies.map((supply, index) => (
                  <tr key={index} className="border-b">
                    <td className="p-3">{supply.item}</td>
                    <td className="p-3">{supply.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex-column">
            <div className="section-title flex items-center text-lg font-semibold mb-4">
              <span className="material-icons section-icon mr-2">flag</span>
              Issues and Follow-ups
            </div>
            {eventData.issues.map((issue, index) => (
              <div key={index} className="activity-card bg-gray-50 p-4 rounded-lg mb-4">
                <div className="activity-header flex justify-between items-center mb-2">
                  <div className="activity-title font-semibold">{issue.title}</div>
                  <div className="activity-time text-gray-600">{issue.priority}</div>
                </div>
                <div className="activity-description text-gray-600 mb-2">{issue.description}</div>
                <div className="tag-container">
                  <div className={`tag ${issue.tagColor} bg-${issue.tagColor}-100 text-${issue.tagColor}-800 px-2 py-1 rounded-full`}>
                    {issue.tag}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="section-card bg-white shadow-md rounded-lg p-6">
        <div className="section-title flex items-center text-lg font-semibold mb-4">
          <span className="material-icons section-icon mr-2">photo_library</span>
          Event Gallery
        </div>
        <div className="gallery-container grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="gallery-item bg-gray-100 p-4 rounded-lg flex justify-center items-center">
              <span className="material-icons text-gray-400 text-4xl">photo</span>
            </div>
          ))}
        </div>
        <div className="section-title flex items-center text-lg font-semibold mb-4">
          <span className="material-icons section-icon mr-2">notes</span>
          Additional Notes
        </div>
        <p className="text-gray-600 mb-4">{eventData.notes}</p>
      </div>
    </>
  );
};

export default VHNDEventSummary;
